import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

export interface DateRange {
  start: string;
  end: string;
}

export interface AnalyticsRequest {
  dateRange?: DateRange;
  metrics?: string[];
  dimensions?: string[];
  limit?: number;
  orderBys?: Array<{ dimension?: string; metric?: string; desc?: boolean }>;
  clientId?: string;
  projectId?: string;
}

export interface AnalyticsResponse {
  propertyId: string;
  dimensions: string[];
  metrics: string[];
  rows: any[];
  totals: any;
}

@Injectable({
  providedIn: 'root'
})
export class AnalyticsDataService {

  constructor(private functions: Functions) { }

  /**
   * Get analytics data (using general endpoint for now)
   */
  getProjectAnalytics(request: AnalyticsRequest): Observable<AnalyticsResponse> {
    // Use analyticsReportSegmented which requires clientId and projectId
    const analyticsReportSegmented = httpsCallable(this.functions, 'analyticsReportSegmented');
    
    return from(analyticsReportSegmented(request)).pipe(
      map((result: any) => {
        // Firebase httpsCallable wraps the response in a 'data' field
        return result.data as AnalyticsResponse;
      }),
      catchError((error) => {
        console.error('Error fetching project analytics:', error);
        throw error;
      })
    );
  }

  /**
   * Get general analytics data (unfiltered)
   */
  getGeneralAnalytics(request: AnalyticsRequest): Observable<AnalyticsResponse> {
    const analyticsReportSegmented = httpsCallable(this.functions, 'analyticsReportSegmented');
    
    return from(analyticsReportSegmented(request)).pipe(
      map((result: any) => {
        console.log('Raw Firebase response:', result);
        // Firebase httpsCallable wraps the response in a 'data' field
        return result.data as AnalyticsResponse;
      }),
      catchError((error) => {
        console.error('Error fetching general analytics:', error);
        throw error;
      })
    );
  }

  /**
   * Get user activity over time
   */
  getUserActivityOverTime(clientId?: string, projectId?: string, dateRange?: DateRange): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: dateRange || this.getDefaultDateRange(420),
      metrics: ['active1DayUsers', 'active7DayUsers', 'active28DayUsers', 'sessions'],
      dimensions: ['date'],
      limit: 100
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get active users in real-time (last 30 minutes)
   */
  getActiveUsersRealTime(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(7), // Last 7 days for more data
      metrics: ['activeUsers'],
      dimensions: ['date'],
      limit: 30
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get users by app version
   */
  getUsersByAppVersion(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: ['activeUsers'],
      dimensions: ['appVersion'], // Use correct GA4 dimension
      limit: 10
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get app stability data
   */
  getAppStability(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: ['crashAffectedUsers', 'crashFreeUsersRate'], // Use crash-related metrics
      dimensions: ['appVersion'],
      limit: 10
    };

    return this.getProjectAnalytics(request);
  }


  /**
   * Get users by country
   */
  getUsersByCountry(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: ['activeUsers'],
      dimensions: ['country'],
      limit: 20
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get users by device model
   */
  getUsersByDevice(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: ['activeUsers', 'sessions'],
      dimensions: ['deviceCategory', 'deviceModel', 'operatingSystem'],
      limit: 15
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get summary metrics
   */
  getSummaryMetrics(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: [
        'activeUsers',           // Total Active Users
        'sessions',              // For calculating Sessions per User
        'sessionsPerUser',       // Sessions per User (direct metric)
        'averageSessionDuration', // Average Session Duration
        'userEngagementDuration', // For calculating Avg Engagement Time
        'crashFreeUsersRate',    // Crash-free Rate
        'engagedSessions',       // Engaged Sessions
        'bounceRate',            // Bounce Rate
        'screenPageViews',       // Page Views
        'newUsers'               // New Users
      ],
      dimensions: [],
      limit: 1
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get session duration distribution data
   */
  getSessionDurationDistribution(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: [
        'sessions',
        'averageSessionDuration',
        'engagedSessions',
        'bounceRate'
      ],
      dimensions: ['sessionDuration'], // This will give us session duration buckets
      limit: 50
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get session engagement level data
   */
  getSessionEngagementLevels(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: [
        'sessions',
        'sessionsPerUser',
        'activeUsers',
        'engagedSessions',
        'engagementRate'
      ],
      dimensions: [], // We'll calculate engagement levels from the totals
      limit: 1
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get detailed session metrics for engagement analysis
   */
  getDetailedSessionMetrics(clientId?: string, projectId?: string): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: this.getDefaultDateRange(420),
      metrics: [
        'sessions',
        'activeUsers',
        'sessionsPerUser',
        'averageSessionDuration',
        'engagedSessions',
        'bounceRate',
        'userEngagementDuration',
        'screenPageViews',
        'eventsPerSession'
      ],
      dimensions: ['newVsReturning'], // This will help us understand user behavior patterns
      limit: 10
    };

    return this.getProjectAnalytics(request);
  }

  /**
   * Get experience loaded events over time
   */
  getExperienceLoadedOverTime(clientId?: string, projectId?: string, dateRange?: DateRange): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: dateRange || this.getDefaultDateRange(420),
      metrics: ['eventCount'],
      dimensions: ['eventName', 'customEvent:experience_name', 'date'],
      limit: 1000,
      orderBys: [{ dimension: 'date', desc: false }]
    };

    return this.getGeneralAnalytics(request);
  }

  /**
   * Get CTA analytics data
   */
  getCtaAnalytics(clientId?: string, projectId?: string, dateRange?: DateRange): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: dateRange || this.getDefaultDateRange(420),
      metrics: ['eventCount'],
      dimensions: ['eventName', 'customEvent:cta_name', 'customEvent:experience_name'],
      limit: 1000
    };

    return this.getGeneralAnalytics(request);
  }

  /**
   * Get experience location data for heatmap
   */
  getExperienceLocationData(clientId?: string, projectId?: string, dateRange?: DateRange): Observable<AnalyticsResponse> {
    const request: AnalyticsRequest = {
      clientId,
      projectId,
      dateRange: dateRange || this.getDefaultDateRange(420),
      metrics: ['eventCount'],
      dimensions: ['eventName', 'customEvent:latitude', 'customEvent:longitude', 'customEvent:experience_name'],
      limit: 1000,
      orderBys: [{ metric: 'eventCount', desc: true }]
    };

    return this.getGeneralAnalytics(request);
  }

  /**
   * Helper method to get default date range
   */
  private getDefaultDateRange(days: number): DateRange {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  }

  /**
   * Format date for GA4 API (YYYYMMDD)
   */
  formatDateForGA4(date: string): string {
    return date.replace(/-/g, '');
  }

  /**
   * Parse GA4 date format (YYYYMMDD) to readable format
   */
  parseGA4Date(dateStr: string): string {
    if (dateStr.length === 8) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  }

  /**
   * Convert seconds to readable time format
   */
  formatDuration(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  }

  /**
   * Format engagement time from total seconds to average per user
   */
  formatEngagementTime(totalEngagementSeconds: number, activeUsers: number): string {
    if (!activeUsers || activeUsers === 0) {
      return '0m 0s';
    }
    
    const avgEngagementSeconds = totalEngagementSeconds / activeUsers;
    return this.formatDuration(Math.round(avgEngagementSeconds));
  }

  /**
   * Calculate sessions per user
   */
  calculateSessionsPerUser(sessions: number, activeUsers: number): number {
    if (!activeUsers || activeUsers === 0) {
      return 0;
    }
    return Number((sessions / activeUsers).toFixed(1));
  }

  /**
   * Calculate session engagement distribution from session metrics
   */
  calculateSessionEngagementDistribution(sessionMetrics: any): {
    highEngagement: number;
    mediumEngagement: number;
    lowEngagement: number;
  } {
    if (!sessionMetrics || !sessionMetrics.sessions || !sessionMetrics.activeUsers) {
      return { highEngagement: 0, mediumEngagement: 0, lowEngagement: 0 };
    }

    const sessionsPerUser = sessionMetrics.sessionsPerUser || this.calculateSessionsPerUser(sessionMetrics.sessions, sessionMetrics.activeUsers);
    const engagementRate = sessionMetrics.engagementRate || 0;
    const bounceRate = sessionMetrics.bounceRate || 0;

    // Calculate engagement levels based on sessions per user and engagement metrics
    // High engagement: 5+ sessions per user or high engagement rate
    const highEngagement = Math.min(Math.round((sessionsPerUser >= 5 ? 40 : 0) + (engagementRate * 30)), 60);
    
    // Low engagement: high bounce rate or 1 session only
    const lowEngagement = Math.min(Math.round(bounceRate * 50 + (sessionsPerUser <= 1 ? 20 : 0)), 40);
    
    // Medium engagement: the remainder
    const mediumEngagement = Math.max(100 - highEngagement - lowEngagement, 20);

    return {
      highEngagement: Math.round(highEngagement),
      mediumEngagement: Math.round(mediumEngagement),
      lowEngagement: Math.round(lowEngagement)
    };
  }

  /**
   * Calculate session duration distribution from session metrics
   */
  calculateSessionDurationDistribution(sessionMetrics: any): {
    quickSessions: number;
    shortSessions: number;
    mediumSessions: number;
    longSessions: number;
  } {
    if (!sessionMetrics || !sessionMetrics.averageSessionDuration) {
      return { quickSessions: 25, shortSessions: 40, mediumSessions: 25, longSessions: 10 };
    }

    const avgDuration = sessionMetrics.averageSessionDuration; // in seconds
    const bounceRate = sessionMetrics.bounceRate || 0;
    const engagementRate = sessionMetrics.engagementRate || 0;

    // Calculate distribution based on average session duration and engagement metrics
    let quickSessions = 25; // <1min
    let shortSessions = 40; // 1-3min
    let mediumSessions = 25; // 3-10min
    let longSessions = 10; // 10min+

    if (avgDuration < 60) {
      // Very short average duration
      quickSessions = Math.min(50 + Math.round(bounceRate * 30), 70);
      shortSessions = Math.max(30 - Math.round(bounceRate * 20), 15);
      mediumSessions = Math.max(15 - Math.round(bounceRate * 10), 10);
      longSessions = Math.max(5 - Math.round(bounceRate * 5), 5);
    } else if (avgDuration < 180) {
      // Short average duration (1-3 minutes)
      quickSessions = Math.min(30 + Math.round(bounceRate * 20), 45);
      shortSessions = Math.min(40 + Math.round(engagementRate * 20), 50);
      mediumSessions = Math.max(20 - Math.round(bounceRate * 10), 15);
      longSessions = Math.max(10 - Math.round(bounceRate * 5), 5);
    } else if (avgDuration < 600) {
      // Medium average duration (3-10 minutes)
      quickSessions = Math.max(20 - Math.round(engagementRate * 10), 10);
      shortSessions = Math.max(35 - Math.round(engagementRate * 10), 25);
      mediumSessions = Math.min(30 + Math.round(engagementRate * 20), 45);
      longSessions = Math.min(15 + Math.round(engagementRate * 10), 25);
    } else {
      // Long average duration (10+ minutes)
      quickSessions = Math.max(15 - Math.round(engagementRate * 10), 5);
      shortSessions = Math.max(25 - Math.round(engagementRate * 15), 15);
      mediumSessions = Math.min(35 + Math.round(engagementRate * 10), 40);
      longSessions = Math.min(25 + Math.round(engagementRate * 20), 40);
    }

    // Ensure percentages add up to 100
    const total = quickSessions + shortSessions + mediumSessions + longSessions;
    if (total !== 100) {
      const adjustment = (100 - total) / 4;
      quickSessions = Math.round(quickSessions + adjustment);
      shortSessions = Math.round(shortSessions + adjustment);
      mediumSessions = Math.round(mediumSessions + adjustment);
      longSessions = Math.round(longSessions + adjustment);
    }

    return {
      quickSessions: Math.max(quickSessions, 5),
      shortSessions: Math.max(shortSessions, 10),
      mediumSessions: Math.max(mediumSessions, 10),
      longSessions: Math.max(longSessions, 5)
    };
  }
}